import React, { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogClose, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Finding, Incident } from "@/app/pages/auditcentre";
import { AuditService } from "@/app/pages/auditcentre/auditService";
import { Textarea } from "../ui/textarea";
import { ControlCombobox } from "../ui/ControlCombobox";
import { MultiUserCombobox } from "../ui/MultiUserCombobox";
import { SingleUserCombobox } from "../ui/SingleUserCombobox";
// DED-1141: Import FrameworkCombobox for framework selection
import { FrameworkCombobox, FrameworksProvider } from "../ui/FrameworkCombobox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useTenancy } from "@/contexts/TenancyContext";
import { MCMMappingsCard, type MappingLink } from "@/components/mcm/MCMMappingsCard";
import { MCMService } from "@/services/mcmService";

type AuditData = {
  type: string;
  scope: string;
  owners: string[];
  auditor: string;
  controls?: string[];
  findings: Finding[];
  incidents: Incident[];
  framework?: string; // DED-1141: Add framework selection
};

type AuditCreateDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onCreate: (audit: Omit<AuditData, "findings" | "incidents"> & {
    findings: Array<Omit<Finding, "id">>;
    incidents: Array<Omit<Incident, "id">>;
  }) => void | string | Promise<void | string>;
  auditService: AuditService;
};

export function AuditCreateDialog({ open, onOpenChange, onCreate, auditService }: AuditCreateDialogProps) {
  const [auditData, setAuditData] = useState<Omit<AuditData, "findings" | "incidents">>({
    type: "",
    scope: "",
    owners: [],
    auditor: "",
    controls: [],
    framework: "", // DED-1141: Add framework selection
  });

  const { userIdToName, activeTenancy } = useTenancy();
  const [findings, setFindings] = useState<Finding[]>([]);
  const [incidents, setIncidents] = useState<Incident[]>([]);
  const [standaloneFindings, setStandaloneFindings] = useState<Finding[]>([]);
  const [standaloneIncidents, setStandaloneIncidents] = useState<Incident[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("details");
  const [newFinding, setNewFinding] = useState<Omit<Finding, "id">>({
    type: "Minor NC",
    status: "Open",
    summary: "",
    owner: "",
    correctiveActions: [],
    origin: 'audit',
  });

  // Load standalone findings and incidents
  useEffect(() => {
    const loadData = async () => {
      try {
        setIsLoading(true);
        const [findingsData, incidentsData] = await Promise.all([
          auditService.listStandaloneFindings(),
          auditService.listStandaloneIncidents()
        ]);
        setStandaloneFindings(findingsData);
        setStandaloneIncidents(incidentsData);
      } catch (error) {
        console.error("Error loading data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    if (open) {
      loadData();
    }
  }, [open, auditService]);

  const resetForm = () => {
    setAuditData({ type: "", scope: "", owners: [], auditor: "", controls: [], framework: "" });
    setFindings([]);
    setIncidents([]);
    setActiveTab("details");
    setNewFinding({ type: "Minor NC", status: "Open", summary: "", owner: "", correctiveActions: [], origin: 'audit' });
  };

  const handleCreateAudit = async () => {
    // Ask parent to create the audit; expect it to return the new auditId (if implemented)
    const payload = {
      ...auditData,
      findings: findings.map(({ id, ...rest }) => rest),
      incidents: incidents.map(({ id, ...rest }) => rest)
    };
    const createdId = await (onCreate(payload) as unknown as Promise<string | void>);
    const auditId = typeof createdId === 'string' ? createdId : undefined;

    try {
      // Persist staged mappings if we have an ID and tenancy
      if (auditId && activeTenancy?.id && Object.keys(stagedMappings).length > 0) {
        const realAuditLink: MappingLink = {
          id: `internal:audit:${auditId}`,
          ref: auditId,
          title: auditData.scope || auditData.type || 'Audit',
          domain: 'Audit',
          relation: 'assesses',
        };
        const isDraftMatch = (lnk: MappingLink) => (lnk.id === pendingAuditLink.id) || (!!lnk.ref && lnk.ref === pendingAuditLink.ref);
        for (const controlId of Object.keys(stagedMappings)) {
          const payloadItem = stagedMappings[controlId];
          const updatedLinks = (payloadItem.links || []).map((lnk) => (isDraftMatch(lnk) ? realAuditLink : lnk));
          await MCMService.bulkUpdate({
            controlIds: [String(controlId)],
            type: 'linkedComponents',
            action: 'update',
            data: { linkedComponents: updatedLinks },
            standard: payloadItem.standard,
          }, activeTenancy.id);
        }
      }
    } finally {
      // Close and reset regardless of mapping outcome
      setStagedMappings({});
      onOpenChange(false);
      resetForm();
    }
  };

  const handleAddFinding = () => {
    setFindings([...findings, { ...newFinding, id: Date.now().toString() }]);
    setNewFinding({
      type: "Minor NC",
      status: "Open",
      summary: "",
      owner: "",
      correctiveActions: [],
      origin: 'audit',
    });
  };

  const handleLinkFinding = (finding: Finding) => {
    if (!findings.some(f => f.id === finding.id)) {
      setFindings([...findings, finding]);
    }
  };

  const handleUnlinkFinding = (findingId: string) => {
    setFindings(findings.filter(f => f.id !== findingId));
  };

  const handleLinkIncident = (incident: Incident) => {
    if (!incidents.some(i => i.id === incident.id)) {
      setIncidents([...incidents, incident]);
    }
  };

  const handleUnlinkIncident = (incidentId: string) => {
    setIncidents(incidents.filter(i => i.id !== incidentId));
  };

  const handleOpenChange = (isOpen: boolean) => {
    if (!isOpen) {
      resetForm();
    }
    onOpenChange(isOpen);
  };

  // Staged MCM mappings (controlId -> { links, standard })
  const [stagedMappings, setStagedMappings] = useState<Record<string, { links: MappingLink[]; standard?: string }>>({});
  // Draft/pending Audit link visible in the mapping card before the Audit exists
  const tempAuditRef = React.useMemo(() => `AUD-DRAFT-${Math.random().toString(36).slice(2, 8).toUpperCase()}`, [open]);
  const pendingAuditLink = React.useMemo<MappingLink>(() => ({
    id: `internal:audit_draft:${tempAuditRef}`,
    ref: tempAuditRef,
    title: auditData.scope || auditData.type || 'Audit',
    domain: 'Audit',
    relation: 'assesses',
  }), [tempAuditRef, auditData.scope, auditData.type]);

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent onOpenAutoFocus={(e) => e.preventDefault()} className={`max-w-3xl! w-full max-h-[90vh] overflow-y-auto rounded-xl shadow-lg border border-white/10 p-0!`} aria-describedby={undefined}>
        <div className="flex flex-col gap-4 h-full bg-card">
          <div className="sticky top-0 z-20 py-6 border-b px-4 border-white/10 bg-gradient-to-b from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60">
            <DialogTitle className="text-foreground">Create New Audit</DialogTitle>
          </div>

          {/* DED-1141: Removed findings and incidents tabs from create dialog - 
              users should create the audit first, then add findings/incidents from the audit detail page */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full px-4 pb-4">
            <TabsList className="grid w-full grid-cols-1">
              <TabsTrigger value="details">Audit Details</TabsTrigger>
            </TabsList>

            <TabsContent value="details" className="space-y-4">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-1">Audit Type</label>
                  <Select value={auditData.type} onValueChange={(value) => setAuditData({ ...auditData, type: value })}>
                    <SelectTrigger className="w-full">
                      <SelectValue placeholder="Select Audit Type" />
                    </SelectTrigger>
                    <SelectContent className="w-full">
                      <SelectItem value="Internal">Internal</SelectItem>
                      <SelectItem value="External">External</SelectItem>
                      <SelectItem value="Compliance">Compliance</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* DED-1141: Add Framework selection */}
                <div>
                  <label className="block text-sm font-medium mb-1">Framework</label>
                  <FrameworksProvider>
                    <FrameworkCombobox
                      selected={auditData.framework ? [auditData.framework] : []}
                      setSelected={(value) => setAuditData({ ...auditData, framework: value[0] || "" })}
                      placeholder="Select framework..."
                      fullWidth
                    />
                  </FrameworksProvider>
                </div>

                <div>
                  {/* DED-1141: Changed from 'Scope Statement' to 'Audit Scope' */}
                  <label className="block text-sm font-medium mb-1">Audit Scope</label>
                  <Textarea
                    value={auditData.scope}
                    onChange={(e) => setAuditData({ ...auditData, scope: e.target.value })}
                    placeholder="Scope of the audit"
                  />
                </div>

                <div className="grid grid-cols-1 items-center gap-4">
                  <Accordion type="single" collapsible defaultValue="controls">
                    <AccordionItem value="controls" className="border rounded-lg shadow-sm mb-2 bg-white dark:bg-background">
                      <AccordionTrigger className="px-4 py-3 text-base font-medium flex items-center gap-2 mb-4">
                        <span className="flex-1 text-left">Controls / Clauses in Scope</span>
                        <span className="ml-auto text-xs text-muted-foreground bg-muted px-2 py-0.5 rounded-full">
                          {Array.isArray(auditData.controls) ? `${auditData.controls.length} selected` : 'None selected'}
                        </span>
                      </AccordionTrigger>
                      <AccordionContent className="px-4 pb-4">
                          <ControlCombobox
                            selected={auditData.controls || []}
                            setSelected={(value) => setAuditData({ ...auditData, controls: (value as Array<string | number>).map(String) })}
                            className="border border-gray-200 rounded-md focus:border-primary focus:ring-1 focus:ring-primary w-full"
                          />
                      </AccordionContent>
                    </AccordionItem>
                    <MCMMappingsCard
                      tenancyId={activeTenancy?.id}
                      isReadOnly={false}
                      controlIds={(auditData.controls || []).map(String)}
                      persistMode="staged"
                      pendingEntityLink={pendingAuditLink}
                      onEnsureControlLinked={(controlId) => setAuditData((prev) => ({
                        ...prev,
                        controls: Array.from(new Set([...(prev.controls || []), String(controlId)])),
                      }))}
                      onApplyStaged={({ controlId, links, standard }) => {
                        setStagedMappings((prev) => ({ ...prev, [controlId]: { links, standard } }));
                      }}
                      collapsible
                    />
                  </Accordion>
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Auditor</label>
                  <Input
                    value={auditData.auditor}
                    onChange={(e) => setAuditData({ ...auditData, auditor: e.target.value })}
                    placeholder="Auditor name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-1">Owners</label>
                  {/* <Input
                    value={auditData.owners.join(", ")}
                    onChange={(e) => setAuditData({...auditData, owners: e.target.value.split(",").map(s => s.trim())})}
                    placeholder="Comma-separated list of owners"
                  /> */}
                  <MultiUserCombobox
                    selected={auditData.owners}
                    setSelected={(value) => setAuditData({ ...auditData, owners: value })}
                  />
                </div>
              </div>
            </TabsContent>

            <TabsContent value="findings" className="space-y-4">
              <div className="space-y-4">
                <div className="border rounded p-4">
                  <h3 className="font-medium mb-2">Add New Finding</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Type</label>
                      <Select value={newFinding.type} onValueChange={(value) => setNewFinding({ ...newFinding, type: value as any })}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Finding Type" />
                        </SelectTrigger>
                        <SelectContent className="w-full">
                          <SelectItem value="Minor NC">Minor NC</SelectItem>
                          <SelectItem value="Major NC">Major NC</SelectItem>
                          <SelectItem value="OFI">OFI</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Status</label>
                      <Select value={newFinding.status} onValueChange={(value) => setNewFinding({ ...newFinding, status: value as any })}>
                        <SelectTrigger className="w-full">
                          <SelectValue placeholder="Select Finding Status" />
                        </SelectTrigger>
                        <SelectContent className="w-full">
                          <SelectItem value="Open">Open</SelectItem>
                          <SelectItem value="Closed">Closed</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Summary</label>
                      <Input
                        value={newFinding.summary}
                        onChange={(e) => setNewFinding({ ...newFinding, summary: e.target.value })}
                        placeholder="Brief description"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Owner</label>
                      <SingleUserCombobox
                        selected={newFinding.owner}
                        setSelected={(value) => setNewFinding({ ...newFinding, owner: value })}
                        placeholder="Owner name"
                      />
                    </div>
                  </div>
                  <Button
                    className="mt-4"
                    onClick={handleAddFinding}
                    disabled={!newFinding.summary || !newFinding.owner}
                  >
                    Add Finding
                  </Button>
                </div>

                <div className="border rounded p-4">
                  <h3 className="font-medium mb-2">Link Existing Findings</h3>
                  {isLoading ? (
                    <div>Loading findings...</div>
                  ) : standaloneFindings.length === 0 ? (
                    <div>No standalone findings found</div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {standaloneFindings.map((finding) => (
                        <div key={finding.id} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <div className="font-medium">{finding.summary}</div>
                            <div className="text-sm text-gray-500">
                              {finding.type} • {finding.status} • {userIdToName?.[finding.owner] || finding.owner}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLinkFinding(finding)}
                            disabled={findings.some(f => f.id === finding.id)}
                          >
                            {findings.some(f => f.id === finding.id) ? 'Linked' : 'Link'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="border rounded p-4">
                  <h3 className="font-medium mb-2">Linked Findings ({findings.length})</h3>
                  {findings.length === 0 ? (
                    <div className="text-gray-500">No findings linked yet</div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {findings.map((finding) => (
                        <div key={finding.id} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <div className="font-medium">{finding.summary}</div>
                            <div className="text-sm text-gray-500">
                              {finding.type} • {finding.status} • {userIdToName?.[finding.owner] || finding.owner}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnlinkFinding(finding.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>

            <TabsContent value="incidents" className="space-y-4">
              <div className="space-y-4">
                <div className="border rounded p-4">
                  <h3 className="font-medium mb-2">Link Existing Incidents</h3>
                  {isLoading ? (
                    <div>Loading incidents...</div>
                  ) : standaloneIncidents.length === 0 ? (
                    <div>No standalone incidents found</div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {standaloneIncidents.map((incident) => (
                        <div key={incident.id} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <div className="font-medium">{incident.summary}</div>
                            <div className="text-sm text-gray-500">
                              {incident.status} • {userIdToName[incident.owner] || incident.owner}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => handleLinkIncident(incident)}
                            disabled={incidents.some(i => i.id === incident.id)}
                          >
                            {incidents.some(i => i.id === incident.id) ? 'Linked' : 'Link'}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div>
                  <h3 className="font-medium mb-2">Linked Incidents ({incidents.length})</h3>
                  {incidents.length === 0 ? (
                    <div className="text-gray-500">No incidents linked yet</div>
                  ) : (
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {incidents.map((incident) => (
                        <div key={incident.id} className="flex items-center justify-between p-2 border rounded">
                          <div>
                            <div className="font-medium">{incident.summary}</div>
                            <div className="text-sm text-gray-500">
                              {incident.status} • {userIdToName[incident.owner] || incident.owner}
                            </div>
                          </div>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleUnlinkIncident(incident.id)}
                          >
                            Remove
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </TabsContent>
          </Tabs>

          <DialogFooter className="mt-4 p-4 sticky bottom-0 z-20 border-white/10 bg-gradient-to-t from-primary/5 to-transparent backdrop-blur supports-[backdrop-filter]:bg-white/60 border-t flex! flex-row! justify-between! items-center! align-center! gap-2">
            <DialogClose asChild>
              <Button variant="outline">Cancel</Button>
            </DialogClose>
            <Button 
              onClick={handleCreateAudit}
              disabled={!auditData.type || !auditData.scope || !auditData.auditor}
            >
              Create Audit
            </Button>
          </DialogFooter>
        </div>
      </DialogContent>
    </Dialog>
  );
}
